// Utility to sync user to backend DBs after Auth0 login
import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

export function useSyncUserOnAuth() {
  const { isAuthenticated, user, getAccessTokenSilently, isLoading } = useAuth0();
  const syncedRef = useRef(false);
  const syncingRef = useRef(false);
  const [syncError, setSyncError] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'success' | 'error'

  useEffect(() => {
    // Don't sync if:
    // - Still loading Auth0 state
    // - Not authenticated
    // - No user object
    // - Already synced
    // - Currently syncing
    if (isLoading || !isAuthenticated || !user || syncedRef.current || syncingRef.current) {
      return;
    }
    
    const syncUser = async () => {
      syncingRef.current = true;
      setSyncStatus('syncing');
      setSyncError(null);
      
      try {
        // Try without audience first to get opaque token, then with audience for JWT
        let accessToken = null;
        
        try {
          const tokenResponse = await getAccessTokenSilently({
            authorizationParams: {
              scope: 'openid profile email'
              // No audience = should return JWT instead of JWE
            },
            detailedResponse: true
          });
          accessToken = tokenResponse.access_token;
        } catch (err) {
          // Fallback to original method
          const tokenResponse = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'openid profile email'
            },
            detailedResponse: true
          });
          accessToken = tokenResponse.access_token;
        }
        
        if (!accessToken) {
          throw new Error('Failed to obtain access token');
        }
        
        // Use the correct backend URL
        const backendUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
        const cleanUrl = backendUrl.replace(/\/$/, '');
        
        // Primary sync: Call user profile endpoint to ensure user exists in MongoDB
        // This will trigger backend middleware which handles user creation/update efficiently
        const profileResponse = await fetch(`${cleanUrl}/api/user/profile`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
            // Send minimal user info in headers - backend will handle user creation if needed
            'x-user-sub': user.sub,
            'x-user-email': user.email || ''
          }
        });
        
        if (!profileResponse.ok) {
          const errorText = await profileResponse.text();
          throw new Error(`Profile sync failed: ${profileResponse.status} ${profileResponse.statusText}`);
        }
        
        const profileData = await profileResponse.json();
        
        // Mark sync as completed - no need for secondary sync
        syncedRef.current = true;
        setSyncStatus('success');
        
      } catch (err) {
        // Don't mark as synced on failure - allow retry on next page load
        syncedRef.current = false;
        setSyncStatus('error');
        setSyncError(err.message || 'Failed to sync user profile');
      } finally {
        syncingRef.current = false;
      }
    };
    
    // Execute sync
    syncUser();
  }, [isAuthenticated, user, getAccessTokenSilently, isLoading]);

  // Reset sync state when user changes or logs out
  useEffect(() => {
    if (!isAuthenticated || !user) {
      syncedRef.current = false;
      syncingRef.current = false;
      setSyncStatus('idle');
      setSyncError(null);
    }
  }, [isAuthenticated, user?.sub]);

  return { syncStatus, syncError };
}
